import GallerySlider from "components/GallerySlider/GallerySlider";
import { currency } from "functions/redux/general/action";
import { FC } from "react";
import { Link } from "react-router-dom";
import { TripTypeBadge } from "./FullTripCard";
import { StarIcon } from "@heroicons/react/solid";
import { useTranslation } from "react-i18next";

const formatTripDates = (start?: string, end?: string) => {
    if (!start) return null;
    const cleanStart = start.replace(/, 12:00 am/gi, '').trim();
    const cleanEnd = end ? end.replace(/, 12:00 am/gi, '').trim() : '';
    if (!cleanEnd || cleanStart === cleanEnd) {
        return cleanStart;
    }
    return `${cleanStart} - ${cleanEnd}`;
};

const NewTripCardRender: FC<{ item: any, imgHeightClass?: string, hideBadge?: boolean }> = ({ item, imgHeightClass = '', hideBadge = false }) => {
    const { t } = useTranslation();

    const bookingCount = Array.isArray(item?.booking) ? item.booking.length : 0;
    const maxGuest = typeof item?.maxGuest === 'number' ? item.maxGuest : (parseInt(item?.maxGuest, 10) || 0);
    const seatLeft = maxGuest - bookingCount;

    if (!item) return <></>;

    const formattedDate = formatTripDates(item?.startTripDate, item?.endTripDate);

    return item?.isOpenTrip && seatLeft < 1 ? <></> : (
        <div 
            className="nc-PropertyCardH group relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-3xl overflow-hidden hover:shadow-xl transition-shadow flex flex-col h-full"
            data-nc-id="PropertyCardH"
        >
            <div className="p-3 w-full">
                <div className={`w-full ${imgHeightClass || 'max-h-96 min-h-32'} relative`}>
                    <GallerySlider
                        ratioClass={`${imgHeightClass ? 'aspect-w-3 aspect-h-3' : 'aspect-w-16 aspect-h-8'}`}
                        galleryImgs={item?.file || []}
                        className="w-full h-full object-cover rounded-2xl overflow-hidden"
                    />
                </div>
            </div>
            {hideBadge === false && (
                <div className="w-28 absolute right-4 top-4 z-10">
                    <TripTypeBadge item={item} />
                </div>
            )}

            <Link to={`detail?q=${item?.slug || ''}`} className="p-4 pt-1 flex flex-col flex-1 justify-between">
                <div className="space-y-2.5">
                    {/* Title */}
                    <h2 className="text-base font-semibold text-neutral-900 dark:text-white line-clamp-2 min-h-[2.75rem] leading-snug group-hover:text-primary-6000 transition-colors">
                        {item?.isOpenTrip ? item?.title : (item?.user?.profile?.displayName || item?.title || 'Guide')}
                    </h2>

                    {/* Date */}
                    {item?.isOpenTrip && formattedDate ? (
                        <div className="flex items-center text-xs font-medium text-neutral-500 dark:text-neutral-400">
                            <i className="las la-calendar text-sm mr-1.5 text-neutral-400"></i>
                            <span>{formattedDate}</span>
                        </div>
                    ) : (
                        <div className="h-4"></div>
                    )}

                    {/* Highlight */}
                    <p className="text-xs md:text-sm text-neutral-500 dark:text-neutral-400 line-clamp-2 leading-relaxed min-h-[2.25rem]">
                        {item?.highlight || ''}
                    </p>

                    {/* Details: places & seats */}
                    <div className="space-y-1.5 pt-1">
                        <div className="flex items-center text-xs text-neutral-600 dark:text-neutral-400">
                            <i className="las la-map-marker-alt text-sm text-neutral-400 mr-1.5 flex-shrink-0"></i>
                            <span className="truncate">
                                {`${Array.isArray(item?.productLocation) ? item.productLocation.length : 0} ${item?.isOpenTrip ? 'place(s) to visit' : 'place(s) suggested'}`}
                            </span>
                        </div>

                        {item?.isOpenTrip ? (
                            <div className="flex items-center text-xs font-medium text-primary-6000">
                                <i className="las la-user-friends text-sm mr-1.5 flex-shrink-0"></i>
                                <span>{t('seats_left', { number: seatLeft })}</span>
                            </div>
                        ) : (
                            <div className="h-4"></div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-100 dark:border-neutral-800/80 rounded-2xl p-3 flex flex-row items-center justify-between mt-4">
                    <div className="flex-1 space-y-0.5 text-center">
                        <p className="text-[11px] font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Service fee</p>
                        <p className="text-base font-bold text-primary-6000">
                            {currency(item?.totalPrice)}
                            <span className="text-xs font-normal text-neutral-400 dark:text-neutral-500 ml-0.5">/ {item?.isOpenTrip ? 'Person' : 'Day'}</span>
                        </p>
                    </div>
                    <div className="w-[1px] h-7 bg-neutral-200 dark:bg-neutral-700/60 mx-1"></div>
                    <div className="flex-1 space-y-0.5 text-center">
                        <p className="text-[11px] font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">Review</p>
                        <div className="flex flex-row justify-center items-center space-x-1">
                            <StarIcon height={16} width={16} className="w-4 h-4 text-amber-400 flex-shrink-0" />
                            <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">No Review</span>
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    );
};

export default NewTripCardRender;